import { useCallback, useState } from 'react';
import { errorMessage } from './api.ts';

/**
 * The Dashboard/Library mutation pattern: one `busy` flag across the page's
 * actions, failures reported via window.alert. `run` executes one mutation
 * (including any reload/toast the caller does inside `fn`), alerts
 * "<failPrefix>: <message>" on failure, and never throws — callers branch on
 * the returned value (undefined = failed).
 */
export function useBusyActions() {
  const [busy, setBusy] = useState(false);
  // Generic so a mutation that returns something keeps its type through `run`.
  // The `?? true` is what makes "undefined = failed" readable at the call site:
  // a void mutation still resolves truthy on success.
  const run = useCallback(
    async <T>(fn: () => T | Promise<T>, failPrefix: string): Promise<NonNullable<T> | true | undefined> => {
      setBusy(true);
      try {
        return (await fn()) ?? true;
      } catch (err) {
        window.alert(`${failPrefix}: ${errorMessage(err)}`);
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [],
  );
  return { busy, run };
}
